import { ApiResponse } from '../types';
import { baseApi } from './baseApi';

export interface ApplicationDocumentRequirement {
  id: string;
  name: string;
  description?: string;
  required: boolean;
  sortOrder: number;
}

export interface ApplicationRegistrationFieldOption {
  value: string;
  label: string;
}

export type ApplicationRegistrationFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'select'
  | 'url'
  | 'email'
  | 'phone'
  | 'date'
  | 'checkbox';

export interface ApplicationRegistrationField {
  id: string;
  label: string;
  type: ApplicationRegistrationFieldType;
  required: boolean;
  placeholder?: string;
  description?: string;
  min?: number;
  max?: number;
  maxLength?: number;
  sortOrder: number;
  options?: ApplicationRegistrationFieldOption[];
}

export interface ApplicationRegistrationFormSection {
  id: string;
  title: string;
  description?: string;
  sortOrder: number;
  fields: ApplicationRegistrationField[];
}

export interface ApplicationSettings {
  newApplicantRegistrationEnabled: boolean;
  requireAdmissionAcceptance: boolean;
  documentRequirements: ApplicationDocumentRequirement[];
  registrationFormSections: ApplicationRegistrationFormSection[];
}

export type SplitPaymentAvailability = 'student_selectable' | (string & {});

export interface SplitPaymentSettings {
  enabled: boolean;
  availability: SplitPaymentAvailability;
  maxInstallments: number;
  defaultIntervalDays: number;
}

export interface FinancialProductsSettings {
  enabled: boolean;
}

export interface FinancialSettings {
  taxRate?: number;
  currency?: string;
  enablePayments?: boolean;
  splitPayment?: Partial<SplitPaymentSettings> | null;
  products?: Partial<FinancialProductsSettings> | null;
}

export interface UpdateFinancialSettingsRequest {
  currency: string;
  taxRate: number;
  enablePayments: boolean;
  splitPayment: SplitPaymentSettings;
  products: FinancialProductsSettings;
}

export interface InstitutionSettings {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  tenantId: string;
  institutionName: string;
  contactInformation?: {
    city?: string;
    email?: string;
    phone?: string;
    state?: string;
    address?: string;
    country?: string;
    website?: string;
    postalCode?: string;
  };
  authSettings?: {
    enableMfa?: boolean;
    enableAccountLockout?: boolean;
  };
  signInMethodsSettings?: {
    passwordLoginEnabled?: boolean;
    otpLoginPortals?: OtpLoginPortal[];
  };
  brandingSettings?: {
    primaryColor?: string;
    secondaryColor?: string;
    logoUrl?: string;
    faviconUrl?: string;
  };
  financialSettings?: FinancialSettings;
  localizationSettings?: {
    dateFormat?: string;
    timeFormat?: '12h' | '24h';
    numberFormat?: {
      decimalPlaces?: number;
      decimalSeparator?: string;
      thousandsSeparator?: string;
    };
    firstDayOfWeek?: number;
    defaultLanguage?: string;
    defaultTimezone?: string;
  };
  nameDisplaySettings?: Record<string, unknown> | null;
  announcementSettings?: Record<string, unknown> | null;
  /** Extra browser origins allowed for CORS (public landers, custom frontends). */
  allowedOrigins?: string[] | null;
  /** Public/guest event registration settings (not portal user registration). */
  eventRegistrationSettings?: {
    blockDisposableEmails?: boolean;
  } | null;
  applicationSettings?: {
    newApplicantRegistrationEnabled?: boolean;
    requireAdmissionAcceptance?: boolean;
    documentRequirements?: ApplicationDocumentRequirement[];
    registrationFormSections?: ApplicationRegistrationFormSection[];
  };
  chatSettings?: {
    enableChat?: boolean;
    maxFileSize?: number;
    blockedUsers?: string[];
    globalDisable?: boolean;
    allowFileSharing?: boolean;
    allowedFileTypes?: string[];
    autoArchiveChats?: boolean;
    requireModeration?: boolean;
    moderationKeywords?: string[];
    allowCourseGroupChat?: boolean;
    allowPublicGroupChat?: boolean;
    archiveRetentionDays?: number;
    allowProjectGroupChat?: boolean;
    allowStaffToStudentDM?: boolean;
    maxParticipantsPerRoom?: number;
    allowStudentToStudentDM?: boolean;
    allowInstructorToStudentDM?: boolean;
    reactionEmojiPolicy?: 'open' | 'allowlist' | 'blocklist';
    reactionEmojiAllowlist?: string[];
    reactionEmojiBlocklist?: string[];
    reactionEmojiDefaultQuickPicks?: string[];
  };
  academicPolicies?: {
    passFail?: {
      allowPartialCredit?: boolean;
      programSpecificCriteria?: unknown[];
      defaultPassingPercentage?: number;
    };
    gradingScale?: {
      scale?: Array<{
        grade: string;
        gradePoint: number;
        description?: string;
        passingGrade: boolean;
        maxPercentage: number;
        minPercentage: number;
      }>;
      type?: string;
      defaultPassingPercentage?: number;
    };
    quizPolicies?: {
      timeLimit?: number;
      shuffleOptions?: boolean;
      shuffleQuestions?: boolean;
      maxRetakeAttempts?: number;
      showCorrectAnswers?: boolean;
      retakeCooldownHours?: number;
      autoSubmitOnTimeExpiry?: boolean;
      allowReviewAfterSubmission?: boolean;
      showCorrectAnswersAfterAttempts?: number;
    };
    taskPolicies?: {
      maxLateDays?: number;
      allowLateSubmission?: boolean;
      autoRejectAfterDeadline?: boolean;
      requireSubmissionComments?: boolean;
      lateSubmissionGracePeriodHours?: number;
      lateSubmissionPenaltyPercentage?: number;
    };
    attendanceRequirements?: {
      countLateAsAbsent?: boolean;
      maxExcusedAbsences?: number;
      allowExcusedAbsences?: boolean;
      requiredForCompletion?: boolean;
      trackPartialAttendance?: boolean;
      minAttendancePercentage?: number;
      lateArrivalThresholdMinutes?: number;
    };
    certificateEligibility?: {
      minGradePercentage?: number;
      minAttendancePercentage?: number;
      minCompletionPercentage?: number;
      requireAttendanceMinimum?: boolean;
      requireAllAssignmentsCompleted?: boolean;
    };
    results?: {
      enableStudentResultSharing?: boolean;
    };
  };
  sessionSecuritySettings?: SessionSecuritySettings;
}

export type SessionSecurityAccountType =
  | 'tenant_institution_student'
  | 'tenant_institution_instructor'
  | 'tenant_institution_staff'
  | 'tenant_institution_admin';

export interface SessionSecuritySettings {
  enabled: boolean;
  applyToAccountTypes: SessionSecurityAccountType[];
  shortWindowHours: number;
  maxUniqueIpsShortWindow: number;
  longWindowDays: number;
  maxUniqueIpsLongWindow: number;
  blockAccountOnLongWindowExceeded: boolean;
  requireConfirmationOnNewIp: boolean;
  maxPrimaryDevices: number;
}

export interface BrandingData {
  logoUrl: string;
  faviconUrl: string;
  primaryColor: string;
  secondaryColor: string;
}


export interface UpdateBrandingPayload {
  primaryColor: string;
  secondaryColor: string;
  logoFileId?: string;
  faviconFileId?: string;
}

export type OtpLoginPortal = 'application' | 'instructor' | 'student';

export interface SignInMethodsSettings {
  passwordLoginEnabled: boolean;
  otpLoginPortals: OtpLoginPortal[];
}

export const institutionApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getInstitutionSettings: builder.query<InstitutionSettings, void>({
      query: () => '/staff/institution/settings',
      transformResponse: (response: { response?: InstitutionSettings; data?: InstitutionSettings } | InstitutionSettings) => {
        if ('response' in response && response.response) return response.response;
        if ('data' in response && response.data) return response.data;
        return response as InstitutionSettings;
      },
      providesTags: ['Institution'],
      extraOptions: { requiredPermission: 'system:config' },
    }),
    updateBasicInformation: builder.mutation<
      ApiResponse<{ institutionName: string }>,
      { institutionName: string }
    >({
      query: (data) => ({
        url: '/staff/institution/settings/basic-information',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Institution'],
      extraOptions: { requiredPermission: 'system:config' },
    }),
    updateContactInformation: builder.mutation<
      ApiResponse<InstitutionSettings['contactInformation']>,
      InstitutionSettings['contactInformation']
    >({
      query: (data) => ({
        url: '/staff/institution/settings/contact-information',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Institution'],
      extraOptions: { requiredPermission: 'system:config' },
    }),
    updateAuthSettings: builder.mutation<
      ApiResponse<{ enableMfa: boolean; enableAccountLockout: boolean }>,
      { enableMfa: boolean; enableAccountLockout: boolean }
    >({
      query: (data) => ({
        url: '/staff/institution/settings/auth',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Institution'],
      extraOptions: { requiredPermission: 'system:config' },
    }),
    updateFinancialSettings: builder.mutation<
      ApiResponse<UpdateFinancialSettingsRequest>,
      UpdateFinancialSettingsRequest
    >({
      query: (data) => ({
        url: '/staff/institution/settings/financial',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Institution'],
      extraOptions: { requiredPermission: 'system:config' },
    }),
    updateLocalizationSettings: builder.mutation<
      ApiResponse<unknown>,
      {
        defaultLanguage: string;
        defaultTimezone: string;
        dateFormat: string;
        timeFormat: string;
        firstDayOfWeek: number;
        numberFormat: {
          decimalSeparator: string;
          thousandsSeparator: string;
          decimalPlaces: number;
        };
      }
    >({
      query: (data) => ({
        url: '/staff/institution/settings/localization',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Institution'],
      extraOptions: { requiredPermission: 'system:config' },
    }),
    updateChatSettings: builder.mutation<
      ApiResponse<unknown>,
      {
        enableChat: boolean;
        globalDisable?: boolean;
        allowStudentToStudentDM: boolean;
        allowInstructorToStudentDM: boolean;
        allowPublicGroupChat?: boolean;
        reactionEmojiPolicy?: 'open' | 'allowlist' | 'blocklist';
        reactionEmojiAllowlist?: string[];
        reactionEmojiBlocklist?: string[];
        reactionEmojiDefaultQuickPicks?: string[];
        allowFileSharing: boolean;
      }
    >({
      query: (data) => ({
        url: '/staff/institution/settings/chat',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Institution'],
      extraOptions: { requiredPermission: 'system:config' },
    }),
    updateAcademicPolicies: builder.mutation<ApiResponse<unknown>, unknown>({
      query: (data) => ({
        url: '/staff/institution/settings/academic-policies',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Institution'],
      extraOptions: { requiredPermission: 'system:config' },
    }),
    updateBranding: builder.mutation<ApiResponse<BrandingData>, UpdateBrandingPayload>({
      query: (data) => ({
        url: '/staff/institution/settings/branding',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Institution'],
      extraOptions: { requiredPermission: 'system:config' },
    }),
    updateSignInMethods: builder.mutation<ApiResponse<SignInMethodsSettings>, SignInMethodsSettings>({
      query: (data) => ({
        url: '/staff/institution/settings/sign-in-methods',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Institution', 'OAuth'],
      extraOptions: { requiredPermission: 'system:config' },
    }),
    updateApplicationSettings: builder.mutation<
      ApiResponse<ApplicationSettings>,
      ApplicationSettings
    >({
      query: (data) => ({
        url: '/staff/institution/settings/application',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Institution'],
      extraOptions: { requiredPermission: 'system:config' },
    }),
    updateSessionSecuritySettings: builder.mutation<
      ApiResponse<SessionSecuritySettings>,
      SessionSecuritySettings
    >({
      query: (data) => ({
        url: '/staff/institution/settings/session-security',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Institution'],
      extraOptions: { requiredPermission: 'system:config' },
    }),
    updateAllowedOrigins: builder.mutation<
      ApiResponse<InstitutionSettings>,
      { allowedOrigins: string[] }
    >({
      query: (data) => ({
        url: '/staff/institution/settings/allowed-origins',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Institution'],
      extraOptions: { requiredPermission: 'system:config' },
    }),
    updateEventRegistrationSettings: builder.mutation<
      ApiResponse<InstitutionSettings>,
      { blockDisposableEmails?: boolean }
    >({
      query: (data) => ({
        url: '/staff/institution/settings/event-registration',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Institution'],
      extraOptions: { requiredPermission: 'system:config' },
    }),
  }),
});

export const {
  useGetInstitutionSettingsQuery,
  useUpdateBasicInformationMutation,
  useUpdateContactInformationMutation,
  useUpdateAuthSettingsMutation,
  useUpdateFinancialSettingsMutation,
  useUpdateLocalizationSettingsMutation,
  useUpdateChatSettingsMutation,
  useUpdateAcademicPoliciesMutation,
  useUpdateBrandingMutation,
  useUpdateApplicationSettingsMutation,
  useUpdateSessionSecuritySettingsMutation,
  useUpdateAllowedOriginsMutation,
  useUpdateEventRegistrationSettingsMutation,
} = institutionApi;

export const useUpdateSignInMethodsMutation = institutionApi.useUpdateSignInMethodsMutation;

